import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Languages } from "lucide-react";
import { useT } from "../../i18n";

export const Navigation = () => {
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const { t, lang, toggle } = useT();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const id = requestAnimationFrame(() =>
            window.scrollTo({ top: 0, left: 0, behavior: "auto" }),
        );
        return () => cancelAnimationFrame(id);
    }, [location.pathname]);

    const links = [
        { to: "/", label: t("nav.overview"), key: "overview" },
        { to: "/video", label: t("nav.video"), key: "video" },
        { to: "/gallery", label: t("nav.gallery"), key: "gallery" },
    ];

    return (
        <header
            data-testid="site-navigation"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-[#E5EAF2]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <span className="w-6 h-6 border border-[#0033A0] flex items-center justify-center bg-white">
                        <span className="w-2 h-2 bg-[#FFC72C] group-hover:bg-[#0033A0] transition-colors" />
                    </span>
                    <span className="font-display text-lg tracking-tight text-[#0033A0]">
                        SAR
                        <span className="inline-block w-1.5 h-1.5 bg-[#FFC72C] mx-1 align-middle rounded-sm" />
                        BOT
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.key}
                            to={l.to}
                            end
                            data-testid={`nav-link-${l.key}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#0033A0]"
                                        : "text-[#595959] hover:text-[#1A1A1A]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3 md:gap-5">
                    <button
                        type="button"
                        onClick={toggle}
                        data-testid="lang-toggle"
                        aria-label="Toggle language"
                        className="inline-flex items-center gap-1.5 border border-[#0033A0]/40 bg-white px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#0033A0] hover:bg-[#FFC72C] hover:border-[#FFC72C] transition-colors"
                    >
                        <Languages size={13} strokeWidth={1.5} />
                        <span className={lang === "en" ? "font-bold" : "opacity-60"}>EN</span>
                        <span className="opacity-40">/</span>
                        <span className={lang === "tr" ? "font-bold" : "opacity-60"}>TR</span>
                    </button>

                    <a
                        href="#methodology"
                        data-testid="nav-cta"
                        className="hidden lg:inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-[#0033A0] hover:text-[#FFC72C] transition-colors"
                    >
                        {t("nav.readPaper")} →
                    </a>
                </div>
            </div>

            {/* Mobile link strip */}
            <nav className="flex md:hidden items-center justify-center gap-5 pb-3 border-t border-[#E5EAF2]/70 pt-2">
                {links.map((l) => (
                    <NavLink
                        key={`m-${l.key}`}
                        to={l.to}
                        end
                        data-testid={`nav-link-mobile-${l.key}`}
                        className={({ isActive }) =>
                            `text-xs uppercase tracking-wider ${
                                isActive ? "text-[#0033A0]" : "text-[#595959]"
                            }`
                        }
                    >
                        {l.label}
                    </NavLink>
                ))}
            </nav>
        </header>
    );
};

export default Navigation;
